import React, { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './SizeSlider.module.scss';

interface Props extends React.HTMLAttributes<HTMLElement> {
    sizes: number[];
    activeStep: number;
    onSetActiveStep: (step: number) => void;
}

function SizeSlider({ sizes, onSetActiveStep, activeStep }: Props) {
    const { t } = useTranslation();

    /**
     * Ref
     */
    const thumbRef = createRef<HTMLDivElement>();
    const thumbShadowRef = createRef<HTMLDivElement>();
    const trackRef = createRef<HTMLDivElement>();
    const stepWrapper = createRef<HTMLDivElement>();

    /**
     * Slider Related
     */
    const thumbWidth = thumbRef.current?.getBoundingClientRect().width || 32;

    /**
     * Window state
     */
    const [mouseCoordinates, setMouseCoordinates] = useState({ x: 0, y: 0 });
    const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

    /**
     * Thumb state
     */
    const [sliderThumbInset, setSliderThumbInset] = useState<number>(0);
    const [sliderThumbPosition, setSliderThumbPosition] = useState<number>(0);
    const [sliderThumbSelected, setSliderThumbSelected] = useState<boolean>(false);
    const [sliderThumbIsMoving, setSliderThumbIsMoving] = useState<boolean>(false);
    const [thumbTransition, setThumbTransition] = useState<string>('none');

    /**
     * Step state
     */
    const [stepXPositions, setStepXPositions] = useState<number[]>([]);

    /**
     * General mouse event handler to track and set the position of the mouse
     * @param event
     */
    const pointerMoveHandler = (event: PointerEvent) => {
        setMouseCoordinates({
            x: event.clientX,
            y: event.clientY,
        });
    };

    const toggleThumbTransition = () => {
        if (!sliderThumbSelected) {
            setThumbTransition('all 0.4s ease');
            setTimeout(() => {
                setThumbTransition('none');
            }, 300);
        }
    };

    const thumbPos = useCallback(() => {
        if (mouseCoordinates.x < stepXPositions[0]) {
            return 0 - thumbWidth / 2;
        } else if (mouseCoordinates.x > stepXPositions[stepXPositions.length - 1]) {
            return (
                stepXPositions[stepXPositions.length - 1] -
                sliderThumbInset -
                thumbWidth / 2
            );
        } else {
            return mouseCoordinates.x - sliderThumbInset - thumbWidth / 2;
        }
    }, [mouseCoordinates.x, sliderThumbInset, stepXPositions, thumbWidth]);

    const closestStep = useMemo(() => {
        const offset = stepXPositions[0] + thumbPos();
        const closest = [...stepXPositions].sort((a: number, b: number) => {
            return Math.abs(a - offset) - Math.abs(b - offset);
        });
        return stepXPositions.indexOf(closest[0]);
    }, [stepXPositions, thumbPos]);

    const onTrackClick = (
        event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
    ) => {
        if (event.type === 'touchstart' || event.type === 'touchend') {
            const e = event as React.TouchEvent<HTMLDivElement>;
            if (e.touches[0]) {
                setMouseCoordinates({
                    x: e.touches[0].clientX,
                    y: e.touches[0].clientY,
                });
            }

            if (e.type === 'touchstart') {
                setSliderThumbSelected(true);
                setSliderThumbPosition(
                    e.touches[0].clientX - sliderThumbInset - thumbWidth / 2
                );
            } else if (e.type === 'touchend') {
                setSliderThumbSelected(false);
            }
        } else {
            if (event.type === 'mousedown' || event.type === 'mouseup') {
                const e = event as React.MouseEvent<HTMLDivElement>;
                if (e.type === 'mousedown' && !sliderThumbSelected) {
                    setSliderThumbPosition(e.clientX - sliderThumbInset - thumbWidth / 2);
                    setSliderThumbSelected(true);
                } else if (e.type === 'mouseup' && sliderThumbSelected) {
                    setSliderThumbSelected(false);
                }
            }
        }
    };

    useEffect(() => {
        window.addEventListener('resize', () => {
            const wSize = { width: window.innerWidth, height: window.innerHeight };
            if (!(windowSize === wSize)) {
                setWindowSize({ width: window.innerWidth, height: window.innerHeight });
            }
        });

        window.addEventListener('pointermove', pointerMoveHandler);
        window.addEventListener('pointerup', () => {
            if (sliderThumbSelected) {
                setSliderThumbSelected(false);
            }
        });
        return () => {
            window.addEventListener('resize', () => {
                const wSize = { width: window.innerWidth, height: window.innerHeight };
                if (!(windowSize === wSize)) {
                    setWindowSize({
                        width: window.innerWidth,
                        height: window.innerHeight,
                    });
                }
            });

            window.addEventListener('pointermove', pointerMoveHandler);
            window.addEventListener('pointerup', () => {
                if (sliderThumbSelected) {
                    setSliderThumbSelected(false);
                }
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!sliderThumbSelected) {
            toggleThumbTransition();
            setSliderThumbPosition(
                stepXPositions[activeStep] - sliderThumbInset - thumbWidth / 2
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep, sliderThumbSelected, stepXPositions]);

    useEffect(() => {
        if (
            stepWrapper.current?.children &&
            stepWrapper.current?.children.length > 0 &&
            stepXPositions.length > 0
        ) {
            setStepXPositions([]);
            if (stepWrapper.current && stepWrapper.current.children.length > 0) {
                for (const ch of stepWrapper.current.children) {
                    setStepXPositions((prev) => [
                        ...prev,
                        ch.getBoundingClientRect().left,
                    ]);
                }
            }
            const trackSize = trackRef.current?.getBoundingClientRect().left || 0;
            setSliderThumbInset(trackSize);
            setSliderThumbPosition(
                stepXPositions[activeStep] - sliderThumbInset - thumbWidth / 2
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize]);

    useEffect(() => {
        if (sliderThumbSelected) {
            onSetActiveStep(closestStep);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [closestStep, sliderThumbSelected]);

    useEffect(() => {
        if (
            stepWrapper.current?.children &&
            stepWrapper.current?.children.length > 0 &&
            stepXPositions.length === 0
        ) {
            setStepXPositions([]);
            if (stepWrapper.current && stepWrapper.current.children.length > 0) {
                for (const ch of stepWrapper.current.children) {
                    setStepXPositions((prev) => [
                        ...prev,
                        ch.getBoundingClientRect().left,
                    ]);
                }
            }

            const thumbFirstPos = thumbRef.current?.getBoundingClientRect().left || 0;
            setSliderThumbInset(thumbFirstPos);
            setSliderThumbPosition(0 - thumbWidth / 2);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepWrapper]);

    useEffect(() => {
        if (sliderThumbSelected) {
            setSliderThumbPosition(thumbPos());
            if (!sliderThumbIsMoving) {
                setSliderThumbIsMoving(true);
            }
        } else {
            if (sliderThumbIsMoving) {
                toggleThumbTransition();
                onSetActiveStep(closestStep);
                setSliderThumbPosition(
                    stepXPositions[closestStep] - sliderThumbInset - thumbWidth / 2
                );
                setSliderThumbIsMoving(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sliderThumbSelected, mouseCoordinates.x]);

    return (
        <div className={`${classes.wrapper}`} data-testid='sizeslider'>
            <div
                className={`${classes.track}`}
                ref={trackRef}
                onMouseDown={onTrackClick}
                onMouseUp={onTrackClick}
                onTouchStart={onTrackClick}
                onTouchEnd={onTrackClick}
            >
                <div
                    className={`${classes.thumb}`}
                    ref={thumbRef}
                    onPointerDown={() => setSliderThumbSelected(true)}
                    onPointerUp={() => setSliderThumbSelected(false)}
                    style={{
                        left: `${sliderThumbPosition}px`,
                        transition: `${thumbTransition}`,
                    }}
                    draggable='false'
                ></div>
                <div className={`${classes.track_container}`}>
                    <div
                        className={`${classes.thumb_shadow}`}
                        ref={thumbShadowRef}
                        style={{
                            left: `${sliderThumbPosition + 12}px`,
                            transition: `${thumbTransition}`,
                        }}
                    ></div>
                </div>
                <div className={`${classes.step_wrapper}`} ref={stepWrapper}>
                    {sizes.map((size: number, index: number) => {
                        return (
                            <div
                                key={`${index}_step_idx`}
                                id={`step_${index}`}
                                className={`${classes.step}`}
                            ></div>
                        );
                    })}
                </div>
            </div>

            <div className={classes.labels}>
                <span>{t('sizeSlider:small')}</span>
                <span>{t('sizeSlider:medium')}</span>
                <span>{t('sizeSlider:large')}</span>
            </div>
        </div>
    );
}

export default SizeSlider;
